import {
  groupForValue,
  type VisualGroup,
} from "../../../questions/visualGroups";
import { Autocomplete, Box, Chip, TextField } from "@mui/material";
import { useRef } from "react";
import { useThemeContext } from "../../../globalContext/global-context";
import { SelectOptions } from "../../../util/types";
import CommonImageComp from "../CommonImageComp";
import {
  autocompleteInputStyle,
  autocompleteStyle,
  autocompleteWrapper,
  optionImageStyle,
  selectedChipStyle,
  selectedMultipleImageStyle,
  selectedMultipleWrapper,
  selectedSingleImageStyle,
  selectedSingleWrapper,
} from "./style";

type AutoCompleteDropdownProps<Value extends number | string> = {
  testId?: string;
  options: SelectOptions<Value>[];
  groups?: readonly VisualGroup<Value>[];
  onSelect: (option: SelectOptions<Value> | SelectOptions<Value>[]) => void;
  onClear?: () => void;
  clearDisabled?: boolean;
  keepOpenOnSelect?: boolean;
  multiple?: boolean;
  autoFocus?: boolean;
  inputBgColor?: string;
  primaryColor: string;
  value?: SelectOptions<Value> | SelectOptions<Value>[] | null;
};

const AutoCompleteDropdown = <Value extends number | string>(
  props: AutoCompleteDropdownProps<Value>,
) => {
  const popupOpenRef = useRef(false);
  const highlightedOptionRef = useRef<SelectOptions<Value> | null>(null);
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const {
    testId,
    options,
    groups,
    onSelect,
    onClear,
    clearDisabled = false,
    keepOpenOnSelect = false,
    multiple = false,
    autoFocus = true,
    primaryColor,
    inputBgColor,
    value,
  } = props;
  const handleClear = () => {
    if (!clearDisabled) onClear?.();
  };
  return (
    <Box data-testid={testId} sx={autocompleteWrapper}>
      <Autocomplete<SelectOptions<Value>, boolean, false, false>
        autoHighlight
        clearOnEscape={!clearDisabled}
        disableCloseOnSelect={multiple || keepOpenOnSelect}
        getOptionDisabled={(option) => option.disabled}
        getOptionKey={(option) => option.value}
        getOptionLabel={(option) => option.text}
        groupBy={
          groups?.length
            ? (option) => groupForValue(groups, option.value)?.text ?? ""
            : undefined
        }
        handleHomeEndKeys
        isOptionEqualToValue={(option, value) => option.value === value.value}
        multiple={multiple}
        noOptionsText={theme.copy.dropdown.noOptions}
        clearText={theme.copy.dropdown.clear}
        closeText={theme.copy.dropdown.close}
        openText={theme.copy.dropdown.open}
        loadingText={theme.copy.dropdown.loading}
        onOpen={() => {
          popupOpenRef.current = true;
        }}
        onClose={() => {
          popupOpenRef.current = false;
          highlightedOptionRef.current = null;
        }}
        onHighlightChange={(_, option) => {
          highlightedOptionRef.current = option;
        }}
        onKeyDown={(event) => {
          const highlightedOption = highlightedOptionRef.current;
          if (
            event.key === "Enter" &&
            !event.nativeEvent.isComposing &&
            popupOpenRef.current &&
            !multiple &&
            highlightedOption !== null &&
            value === highlightedOption
          )
            onSelect(highlightedOption);
        }}
        onChange={(_, option) => {
          if (multiple) {
            if (Array.isArray(option) && option.length > 0) {
              onSelect(option);
            } else {
              handleClear();
            }
          } else if (option) {
            onSelect(option as SelectOptions<Value>);
          } else {
            handleClear();
          }
        }}
        options={options}
        slotProps={{ clearIndicator: { disabled: clearDisabled } }}
        value={value}
        renderValue={(value, getItemProps) => {
          /* v8 ignore start */
          const selectedValues = Array.isArray(value)
            ? value
            : value
              ? [value]
              : [];
          /* v8 ignore end */
          if (!multiple) {
            const option = selectedValues[0] as
              | SelectOptions<Value>
              | undefined;
            /* c8 ignore next -- MUI only invokes renderValue for a non-null value. */
            if (!option) return null;

            return (
              <Box sx={selectedSingleWrapper}>
                <CommonImageComp
                  currentVal={option}
                  isCheckbox
                  disablePreviewWrapper
                  imageTestid={`autocomplete-selected-image-${option.value}`}
                  imageStyle={selectedSingleImageStyle}
                />
              </Box>
            );
          }

          return selectedValues.map((option, index) => {
            const itemPropsWithKey = getItemProps({ index }) as ReturnType<
              typeof getItemProps
            > & { key?: string | number };
            const { key: itemKey, ...itemProps } = itemPropsWithKey;

            return (
              <Chip
                key={itemKey ?? (option as SelectOptions<Value>).value}
                {...itemProps}
                label={
                  <Box sx={selectedMultipleWrapper}>
                    <CommonImageComp
                      currentVal={option as SelectOptions<Value>}
                      isCheckbox
                      disablePreviewWrapper
                      imageTestid={`autocomplete-selected-image-${(option as SelectOptions<Value>).value}`}
                      imageStyle={selectedMultipleImageStyle}
                    />
                  </Box>
                }
                sx={selectedChipStyle}
              />
            );
          });
        }}
        renderOption={(props, option) => {
          const { key, onClick, ...optionProps } = props;
          const isCurrentSelection =
            !multiple && !Array.isArray(value) && value === option;

          return (
            <li
              key={key}
              {...optionProps}
              onClick={(event) => {
                onClick?.(event);
                if (isCurrentSelection) onSelect(option);
              }}
            >
              <CommonImageComp
                currentVal={option}
                isCheckbox
                disablePreviewWrapper
                imageStyle={optionImageStyle}
              />
            </li>
          );
        }}
        sx={autocompleteStyle}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus={autoFocus}
            data-testid="modal-select-autocomplete"
            label={theme.copy.dropdown.label}
            placeholder={theme.copy.dropdown.placeholder}
            slotProps={{
              ...params.slotProps,
              inputLabel: {
                ...params.slotProps.inputLabel,
                shrink: true,
              },
            }}
            sx={autocompleteInputStyle(theme, primaryColor, inputBgColor)}
          />
        )}
      />
    </Box>
  );
};

export default AutoCompleteDropdown;
